import React, { useMemo, useState } from 'react';

import { useAdminAiUsageTurns } from '@dashboard/hooks/queries/useAdminQueries';
import useUrlState from '@dashboard/hooks/useUrlState';

import { DataGrid, DataGridColumnHeader } from '@dashboard/components/DataGrid';
import EmptyState from '@dashboard/components/EmptyState';
import { Label } from '@dashboard/components/Form';
import Link from '@dashboard/components/Link';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@dashboard/components/ui/select';
import { Badge } from '@dashboard/components/ui/badge';
import { Button } from '@dashboard/components/ui/button';
import { Checkbox } from '@dashboard/components/ui/checkbox';

import AiUsageTurnSheet from './AiUsageTurnSheet';
import {
  AI_USAGE_PLAN_LABELS,
  AI_USAGE_STATUS_BADGE_VARIANTS,
  AI_USAGE_STATUS_LABELS,
  AI_USAGE_SURFACE_LABELS,
  aiUsagePageHref,
  aiUsageStoreHref,
  formatTurnTime,
} from './aiUsageLabels';
import { type AiUsagePeriod, type AiUsageRange, formatMicros } from './aiUsagePeriod';

export type AiUsageTurnRow = {
  _id: string;
  createdAt: string;
  surface: string;
  plan: string;
  status: string;
  costMicros: number;
  totalTokens: number;
  offTopic: boolean;
  topic?: string;
  prompt?: string;
  reply?: string;
  tools?: { name: string; ok?: boolean }[];
  error?: string;
  changes?: unknown[];
  pageType?: string;
  page?: string;
  seller?: { _id: string; name?: string; displayName?: string } | null;
  user?: { _id: string; email?: string } | null;
  conversationId?: string;
  attachments?: AiUsageTurnAttachment[];
};

export type AiUsageTurnAttachment = {
  fileId: string;
  name: string;
  type: string;
  previewUrl?: string;
  downloadUrl?: string;
};

type Props = { filters: AiUsageRange & { seller?: string }; period?: AiUsagePeriod };

const empty = <span className="text-zinc-400 dark:text-muted-foreground">-</span>;

// A page's turns can predate the period picked for the store, so this filter looks at all of them.
const ALL_TIME_FROM = new Date(0).toISOString();

const labelledValue = (labels: Record<string, string>, value: unknown) =>
  typeof value === 'string' && labels[value] ? value : undefined;

const AiUsageTurnsTable = ({ filters, period }: Props) => {
  const [query, setQuery] = useUrlState() as [
    Record<string, unknown>,
    (updates: Record<string, unknown>) => void,
  ];

  const surface = labelledValue(AI_USAGE_SURFACE_LABELS, query.surface);
  const status = labelledValue(AI_USAGE_STATUS_LABELS, query.status);
  const offTopic = query.offTopic === true;
  const seller = filters.seller ?? (typeof query.seller === 'string' ? query.seller : undefined);
  const pageId = typeof query.pageId === 'string' ? query.pageId : undefined;

  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage } = useAdminAiUsageTurns({
    from: pageId ? ALL_TIME_FROM : filters.from,
    to: pageId ? undefined : filters.to,
    seller,
    page: pageId,
    surface,
    status,
    offTopic,
  });

  const flatData: AiUsageTurnRow[] = useMemo(
    () => data?.pages?.flatMap((page: any) => page.data) ?? [],
    [data],
  );

  const total: number | undefined = data?.pages[0]?.total;

  const [openTurnId, setOpenTurnId] = useState<string>();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);

  const openIndex = flatData.findIndex(row => row._id === openTurnId);
  const openTurn = openIndex === -1 ? undefined : flatData[openIndex];
  const newerTurn = openIndex > 0 ? flatData[openIndex - 1] : undefined;
  const olderTurn = openIndex === -1 ? undefined : flatData[openIndex + 1];
  const canLoadOlder = openIndex !== -1 && !olderTurn && hasNextPage;

  const showOlder = async () => {
    if (olderTurn) {
      setOpenTurnId(olderTurn._id);
      return;
    }
    if (!canLoadOlder || loadingOlder) return;
    setLoadingOlder(true);
    try {
      const result = await fetchNextPage();
      const rows: AiUsageTurnRow[] =
        result.data?.pages?.flatMap((page: any) => page.data) ?? flatData;
      const next = rows[rows.findIndex(row => row._id === openTurnId) + 1];
      if (next) setOpenTurnId(next._id);
    } finally {
      setLoadingOlder(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'createdAt',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Time" />,
        cell: ({ row }: any) => (
          <span className="whitespace-nowrap">{formatTurnTime(row.original.createdAt)}</span>
        ),
      },
      {
        accessorKey: 'seller',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Store" />,
        cell: ({ row }: any) => {
          const { seller: rowSeller } = row.original as AiUsageTurnRow;
          if (!rowSeller) return empty;
          return (
            <Link to={aiUsageStoreHref(rowSeller._id, period)}>
              {rowSeller.displayName || rowSeller.name}
            </Link>
          );
        },
      },
      {
        accessorKey: 'user',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="User email" />,
        cell: ({ row }: any) => row.original.user?.email || empty,
      },
      {
        accessorKey: 'prompt',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Prompt" />,
        cell: ({ row }: any) => {
          const turn = row.original as AiUsageTurnRow;
          return (
            <div className="min-w-56 max-w-xs space-y-1">
              <button
                type="button"
                className="block cursor-pointer text-left font-medium transition-colors hover:text-muted-foreground"
                title={turn.prompt}
                onClick={() => {
                  setOpenTurnId(turn._id);
                  setSheetOpen(true);
                }}
              >
                {turn.prompt ? (
                  <span className="line-clamp-2">{turn.prompt}</span>
                ) : (
                  <span className="text-muted-foreground">No prompt saved</span>
                )}
              </button>
              {turn.offTopic && (
                <div className="flex items-center gap-x-2 text-sm">
                  <Badge variant="warning">Off-topic</Badge>
                  {turn.topic && (
                    <span className="text-gray-500 dark:text-muted-foreground">{turn.topic}</span>
                  )}
                </div>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'surface',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Surface" />,
        cell: ({ row }: any) =>
          AI_USAGE_SURFACE_LABELS[row.original.surface] ?? row.original.surface,
      },
      {
        accessorKey: 'plan',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Plan" />,
        cell: ({ row }: any) => AI_USAGE_PLAN_LABELS[row.original.plan] ?? row.original.plan,
      },
      {
        accessorKey: 'costMicros',
        header: ({ column }: any) => (
          <DataGridColumnHeader column={column} title="Estimated cost" />
        ),
        cell: ({ row }: any) => formatMicros(row.original.costMicros),
      },
      {
        accessorKey: 'totalTokens',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Tokens" />,
        cell: ({ row }: any) => row.original.totalTokens.toLocaleString(),
      },
      {
        accessorKey: 'status',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Status" />,
        cell: ({ row }: any) => {
          const { status: rowStatus } = row.original as AiUsageTurnRow;
          return (
            <Badge variant={AI_USAGE_STATUS_BADGE_VARIANTS[rowStatus] ?? 'outline'}>
              {AI_USAGE_STATUS_LABELS[rowStatus] ?? rowStatus}
            </Badge>
          );
        },
      },
      {
        accessorKey: 'page',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Page" />,
        cell: ({ row }: any) => {
          const { page } = row.original as AiUsageTurnRow;
          if (!page) return null;
          return (
            <Link to={aiUsagePageHref(page)} target="_blank" rel="noopener noreferrer">
              View page
            </Link>
          );
        },
      },
    ],
    [period],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-end gap-x-2">
        <div className="mr-auto flex h-9 items-center text-sm text-gray-500 dark:text-muted-foreground">
          {total !== undefined && `${total} ${total === 1 ? 'turn' : 'turns'}`}
          {pageId && (
            <>
              <span className="mx-1">on one page, all time</span>
              <Button variant="link" size="xs" onClick={() => setQuery({ pageId: undefined })}>
                Show all pages
              </Button>
            </>
          )}
        </div>

        <div>
          <Label htmlFor="surface">Surface</Label>
          <Select
            name="surface"
            value={surface ?? 'all'}
            onValueChange={value => setQuery({ surface: value === 'all' ? undefined : value })}
          >
            <SelectTrigger id="surface" className="!w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All surfaces</SelectItem>
              {Object.entries(AI_USAGE_SURFACE_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="status">Status</Label>
          <Select
            name="status"
            value={status ?? 'all'}
            onValueChange={value => setQuery({ status: value === 'all' ? undefined : value })}
          >
            <SelectTrigger id="status" className="!w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {Object.entries(AI_USAGE_STATUS_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <label className="flex h-9 items-center gap-x-1.5 whitespace-nowrap text-sm cursor-pointer">
          <Checkbox
            checked={offTopic}
            onCheckedChange={checked => setQuery({ offTopic: checked === true || undefined })}
          />
          Off-topic only
        </label>
      </div>

      <DataGrid
        columns={columns}
        data={flatData}
        isLoading={isLoading}
        paginationMode="infinite"
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        getRowId={row => row._id}
        emptyState={
          <EmptyState>
            <EmptyState.Title>No turns in this period</EmptyState.Title>
          </EmptyState>
        }
      />

      <AiUsageTurnSheet
        turn={openTurn}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        onNewer={newerTurn ? () => setOpenTurnId(newerTurn._id) : undefined}
        onOlder={olderTurn || canLoadOlder ? showOlder : undefined}
        loadingOlder={loadingOlder}
      />
    </div>
  );
};

export default AiUsageTurnsTable;
